import { useEventStore } from '../context/EventContext';
import { RotationTreeEnum } from '../enums/rotationTreeEnum';
import { CargoOperation } from '../types/ViewModel/CargoOperation';
import { DropdownTypeEnum } from '../enums/dropdownTypeEnum';
import { TankersEvent } from '../types/ViewModel/TankersEvent';
import { BerthCall } from '../types/ViewModel/BerthCall';
import { IRotationNode } from '../types/ViewModel/IRotationNode';
import TankersEventFactory from '../factory/TankersEventFactory';
import useRotation from './useRotation';
import { PortCall } from '../types/ViewModel/PortCall';
import { EOSP, SOSP } from '../types/ViewModel/TankersEventType';
import { Rotation } from '../types/ViewModel/Rotation';
import RotationFactory from '../factory/ReportEvents/Rotation/RotationFactory';
import { ReportLocationEnum } from '../enums/reportLocationEnum';
import { useRotationStore } from '../context/RotationContext';
import { TankersEventDataBM } from '../types/BusinessModel/TankersEventDataBM';
import { MoveEventsToCargoOperationBM } from '../types/BusinessModel/MoveEventsToCargoOperationBM';
import { MoveEventsToBerthCallBM } from '../types/BusinessModel/MoveEventsToBerthCallBM';
import { MoveEventsPortCallBM } from '../types/BusinessModel/MoveEventsPortCallBM';
import { EventType } from '../types/ViewModel/EventType';
import { EventTypeGroup } from '../types/ViewModel/EventTypeGroup';
import eventtypecommon from '../utils/event/eventtypecommon';
import { EventTypeInput } from '../types/ViewModel/EventTypeInput';
import { EventTypeInputDelayEventTypeMap } from '../types/ViewModel/EventTypeInputDelayEventTypeMap';
import { compareActualEventDateTime, getDateWithoutSecMs } from '../utils/event/eventDateUtils';
import { isDateAfter, isDateBefore, isSameDate, subtractDateMinutes, addDateMinutes, getUtcTimeNow } from 'odfjell-date-time-utils/dateTimeUtils';

export default () => {
    const { newRotationFromCode } = RotationFactory();
    const { newEventList } = TankersEventFactory();

    const {
        getParentPortCallFromNode,
        getBerthCallFromNode,
        getParentBerthCallFromNode,
        getCargoOperationFromNode,
        getPortCallFromNode,
        getPortCallIndex,
        getBerthCallIndex,
        getCargoOperationIndex,
        getPortCall,
        getBerthCall,
        getCargoOperation,
    } = useRotation();
    const { entityOk } = eventtypecommon();

    const { updatedRotation } = useRotationStore();

    const { fullEventArray, setDeltaEventArray, setFirstPortCallEventTime } = useEventStore();

    const getFirstPortCallTime = (node: IRotationNode | null, eventArray: TankersEvent[]): Date | null => {
        let firstTime: Date | null = null;
        if (node) {
            const portCall = getPortCallFromNode(node);
            if (portCall) {
                const portCallEvList = filterOnContext(portCall, eventArray);
                if (portCallEvList && portCallEvList.length > 0) {
                    const evParentSorted = portCallEvList.sort((a, b) => compareActualEventDateTime(a, b, false));
                    firstTime = evParentSorted[0].eventDate.actualDateTimeLocal;
                }
            }
        }
        setFirstPortCallEventTime(firstTime);
        return firstTime;
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const updateDependentEvents = (eventTypeGroupSequenceIdentity: string, updatedEvent: TankersEvent, evList: TankersEvent[]) => {
        // Update events in same sequence
        const evListSameSeq = evList.filter((o) => o.eventTypeGroupSequenceIdentity === eventTypeGroupSequenceIdentity);
        evListSameSeq.forEach((ev) => {
            ev.dependencyDisplayName = updatedEvent.dependencyDisplayName;
            ev.lastUpdated = updatedEvent.lastUpdated;
            ev.updatedBy = updatedEvent.updatedBy;
        });

        // Find children events
        const evListChildren = evList.filter(
            (o) => o.eventTypeGroupSequenceDependencyIdentity && o.eventTypeGroupSequenceDependencyIdentity === eventTypeGroupSequenceIdentity
        );
        if (!evListChildren || evListChildren.length === 0) {
            // Exit if no children
            return;
        }

        // Update events for children
        evListChildren.forEach((ev) => {
            ev.dependencyDisplayName = updatedEvent.dependencyDisplayName;
            ev.lastUpdated = updatedEvent.lastUpdated;
            ev.updatedBy = updatedEvent.updatedBy;
        });

        // Find unique secuences for children
        let grSeqList = evListChildren.map((o) => o.eventTypeGroupSequenceIdentity);
        grSeqList = grSeqList.filter(function (v, i) {
            return grSeqList.indexOf(v) === i;
        });

        // Call this method recursively for all sequences
        grSeqList.forEach((seq) => updateDependentEvents(seq, updatedEvent, evListChildren));
        return;
    };

    const isWithinDelayPeriod = (evDateTime: Date, startDelay: Date, stopDelay: Date): boolean =>
        isDateAfter(evDateTime, getDateWithoutSecMs(startDelay)) && isDateBefore(evDateTime, getDateWithoutSecMs(stopDelay));

    const filterEvListCompletedSeq = (evType: EventType, evList: TankersEvent[]): TankersEvent[] => {
        const totalEvList: TankersEvent[] = [];
        const startEvList = evList.filter((ev) => {
            return Boolean(evType.eventTypeInputExcludeTimeEventTypeMapArray.find((o) => o.fromEventTypeId === ev.eventTypeId));
        });
        startEvList.forEach((evFrom) => {
            const evTo = evList.find((ev) => {
                return (
                    Boolean(evType.eventTypeInputExcludeTimeEventTypeMapArray.find((p) => p.toEventTypeId === ev.eventTypeId)) &&
                    ev.eventTypeGroupSequenceIdentity === evFrom.eventTypeGroupSequenceIdentity
                );
            });
            if (evTo) {
                totalEvList.push(evFrom);
                totalEvList.push(evTo);
            }
        });
        return totalEvList.sort((a, b) => compareActualEventDateTime(a, b, false));
    };

    const excludeDelay = (evType: EventType, evList: TankersEvent[], startDelay: Date, stopDelay: Date): number => {
        let diffMs = 0;
        let excludeEvList = evList.filter((ev) => {
            const excludeEvent = evType.eventTypeInputExcludeTimeEventTypeMapArray.find((o) => {
                if (
                    (o.fromEventTypeId === ev.eventTypeId || o.toEventTypeId === ev.eventTypeId) &&
                    isWithinDelayPeriod(ev.eventDate?.actualDateTimeLocal, startDelay, stopDelay)
                )
                    return true;
                return false;
            });
            return Boolean(excludeEvent);
        });

        if (excludeEvList && excludeEvList.length > 0) {
            excludeEvList = filterEvListCompletedSeq(evType, excludeEvList);
            let noOfPeriods = 0;
            let startPeriod: Date | null = null;
            excludeEvList.forEach((ev) => {
                const isStartEv = evType.eventTypeInputExcludeTimeEventTypeMapArray.find((o) => o.fromEventTypeId === ev.eventTypeId);
                if (isStartEv) {
                    noOfPeriods++;
                    if (!startPeriod) {
                        startPeriod = ev.eventDate?.actualDateTimeLocal;
                    }
                } else {
                    noOfPeriods--;
                }
                if (noOfPeriods === 0) {
                    diffMs += ev.eventDate.actualDateTimeLocal?.getTime() - startPeriod?.getTime();
                    startPeriod = null;
                }
            });
        }
        return Math.floor(diffMs / (1000 * 60));
    };

    // toDelayEvList: List of Pumping Commence (cargo) or List of Gangway up (berth)
    // evList: List of context related events
    // Action:
    //      -- find earliest for each cargo, with excludes
    //      -- excludes for this node only
    //      -- event list for this node only

    const getCheckDate = (existingEvent: TankersEvent | null, currentEvent: TankersEvent, evDateTimeLocal: Date): Date => {
        if (existingEvent) {
            if (existingEvent.eventId === currentEvent.eventId) {
                return evDateTimeLocal;
            }
        }
        return currentEvent.eventDate.actualDateTimeLocal;
    };

    // toDelayEvList: List of Pumping Commence (cargo) or List of Gangway up (berth)
    // evList: List of context related events
    // Action:
    //      -- find earliest for each cargo, with excludes
    //      -- excludes for this node only
    //      -- event list for this node only
    const findEarliestToEventList = (
        nodeEvList: TankersEvent[],
        delayTime: number,
        from: Date,
        evType: EventType,
        evDateTimeLocal: Date,
        existingEvent: TankersEvent | null
    ): [TankersEvent[], Date | null] => {
        let earliestEvList: TankersEvent[] = [];
        let earliestDate: Date | null = null;

        const toDelayEvList = nodeEvList.filter((o) => o.eventTypeId === evType.eventTypeId);
        toDelayEvList.forEach((ev) => {
            const dateWithExcludes = findAdjustedToTime(nodeEvList, delayTime, from, getCheckDate(existingEvent, ev, evDateTimeLocal), evType);

            if (!earliestDate) {
                earliestDate = dateWithExcludes;
                earliestEvList.push(ev);
            } else {
                if (isSameDate(earliestDate, dateWithExcludes)) {
                    earliestEvList.push(ev);
                } else if (isDateBefore(getDateWithoutSecMs(dateWithExcludes), earliestDate)) {
                    earliestEvList = [];
                    earliestEvList.push(ev);
                    earliestDate = dateWithExcludes;
                }
            }
        });
        return [earliestEvList, earliestDate];
    };

    // toDelayEvList: List of Pumping Commence (cargo) or List of Gangway up (berth)
    // evList: List of context related events
    // Action:
    //      -- find earliest for each cargo, with excludes
    //      -- excludes for this node only
    //      -- event list for this node only
    const findLatestFromEventList = (
        nodeEvList: TankersEvent[],
        inpDelay: EventTypeInputDelayEventTypeMap,
        to: Date,
        evType: EventType,
        evDateTimeLocal: Date,
        existingEvent: TankersEvent | null
    ): [TankersEvent[], Date | null] => {
        let latestEvList: TankersEvent[] = [];
        let latestDate: Date | null = null;

        const fromDelayEvList = nodeEvList.filter((o) => o.eventTypeId === inpDelay.startDelayEventTypeId);
        fromDelayEvList.forEach((ev) => {
            const dateWithExcludes = findAdjustedFromTime(
                nodeEvList,
                inpDelay.delayTimeMinutes,
                getCheckDate(existingEvent, ev, evDateTimeLocal),
                to,
                evType
            );

            if (!latestDate) {
                latestDate = dateWithExcludes;
                latestEvList.push(ev);
            } else {
                if (isSameDate(latestDate, dateWithExcludes)) {
                    latestEvList.push(ev);
                } else if (isDateAfter(getDateWithoutSecMs(dateWithExcludes), latestDate)) {
                    latestEvList = [];
                    latestEvList.push(ev);
                    latestDate = dateWithExcludes;
                }
            }
        });
        return [latestEvList, latestDate];
    };

    const findAdjustedToTime = (nodeEvList: TankersEvent[], delayTime: number, from: Date, to: Date, evType: EventType): Date => {
        const delayLimit = subtractDateMinutes(to, delayTime);
        const excludeMinutes = excludeDelay(evType, nodeEvList, from, to);
        return subtractDateMinutes(delayLimit, excludeMinutes);
    };

    const findAdjustedFromTime = (nodeEvList: TankersEvent[], delayTime: number, from: Date, to: Date, evType: EventType): Date => {
        const delayLimit = addDateMinutes(from, delayTime);
        const excludeMinutes = excludeDelay(evType, nodeEvList, from, to);
        return addDateMinutes(delayLimit, excludeMinutes);
    };

    const isDelayExceeded = (
        rot: Rotation | null,
        evType: EventType,
        evList: TankersEvent[],
        evTypeInput: EventTypeInput,
        evDateTimeLocal: Date,
        existingEvent: TankersEvent | null,
        node: IRotationNode | null
    ): EventTypeInputDelayEventTypeMap | null => {
        let selNode: IRotationNode | null = null;
        if (rot) {
            if (node) {
                selNode = node;
            } else {
                selNode = getRotationNodeFromEvent(rot, existingEvent);
            }
            if (selNode) {
                const inpDelayList = evType.eventTypeInputDelayEventTypeMapArray.filter((o) => o.eventTypeInputId === evTypeInput.eventTypeInputId);
                if (inpDelayList && inpDelayList.length > 0) {
                    let keepGoing = true;
                    let inpDelayInd = 0;
                    while (keepGoing) {
                        const fromDelayEvList = evList.filter((o) => o.eventTypeId === inpDelayList[inpDelayInd].startDelayEventTypeId);
                        if (fromDelayEvList && fromDelayEvList.length > 0) {
                            const selNodeEvList = filterOnContextIncludeParent(selNode, evList);
                            const fromNode = getRotationNodeFromEvent(rot, fromDelayEvList[0]);
                            if (fromNode) {
                                if (selNode.type < fromNode.type) {
                                    // Find all unique nodes for events in selNodeEvList, e.g. for all Pumping Commence
                                    const nList = selNodeEvList
                                        .filter((ev) => {
                                            const fNode = getRotationNodeFromEvent(rot, ev);
                                            if (fNode && fNode.type === fromNode?.type) return true;
                                            return false;
                                        })
                                        .map((o) => {
                                            return getRotationNodeFromEvent(rot, o);
                                        });
                                    const nodeList = nList.filter(function (v, i) {
                                        return nList.indexOf(v) === i;
                                    });
                                    return isDelayExceededCompletion(
                                        rot,
                                        evType,
                                        evList,
                                        inpDelayList[inpDelayInd],
                                        evDateTimeLocal,
                                        existingEvent,
                                        selNode,
                                        nodeList
                                    );
                                }
                                return isDelayExceededPreparation(
                                    evType,
                                    evList,
                                    inpDelayList[inpDelayInd],
                                    evDateTimeLocal,
                                    existingEvent,
                                    selNodeEvList
                                );
                            }
                        }
                        inpDelayInd++;
                        if (inpDelayInd === inpDelayList.length) {
                            keepGoing = false;
                        }
                    }
                }
            }
        }
        return null;
    };

    const isDelayExceededPreparation = (
        evType: EventType,
        evList: TankersEvent[],
        inpDelay: EventTypeInputDelayEventTypeMap,
        evDateTimeLocal: Date,
        existingEvent: TankersEvent | null,
        selNodeEvList: TankersEvent[]
    ): EventTypeInputDelayEventTypeMap | null => {
        const frDelayEvList = selNodeEvList.filter((o) => o.eventTypeId === inpDelay.startDelayEventTypeId);
        const latestEv = findLatestEvent(frDelayEvList);
        const toDateAdjustedWithDelayTime = findAdjustedToTime(
            selNodeEvList,
            inpDelay.delayTimeMinutes,
            latestEv.eventDate.actualDateTimeLocal,
            evDateTimeLocal,
            evType
        );
        const fromDelayEvList = evList.filter((o) => o.eventTypeId === inpDelay.startDelayEventTypeId);
        if (fromDelayEvList && fromDelayEvList.length > 0) {
            const [earliestToEvList, earliestToDate] = findEarliestToEventList(
                selNodeEvList,
                inpDelay?.delayTimeMinutes,
                latestEv.eventDate?.actualDateTimeLocal,
                evType,
                evDateTimeLocal,
                existingEvent
            );
            if (existingEvent) {
                const earliestEv = earliestToEvList.find((o) => o.eventId === existingEvent.eventId);
                if (!earliestEv) {
                    return null;
                }
                if (isDateBefore(getDateWithoutSecMs(toDateAdjustedWithDelayTime), latestEv.eventDate.actualDateTimeLocal)) {
                    return null;
                }
            } else {
                if (earliestToDate && isDateAfter(getDateWithoutSecMs(toDateAdjustedWithDelayTime), earliestToDate)) {
                    return null;
                }
                if (isDateBefore(getDateWithoutSecMs(toDateAdjustedWithDelayTime), latestEv.eventDate.actualDateTimeLocal)) {
                    return null;
                }
            }
            return inpDelay;
        }
        return null;
    };

    const isDelayExceededCompletion = (
        rot: Rotation,
        evType: EventType,
        evList: TankersEvent[],
        inpDelay: EventTypeInputDelayEventTypeMap,
        evDateTimeLocal: Date,
        existingEvent: TankersEvent | null,
        selNode: IRotationNode,
        nodeList: (IRotationNode | null)[]
    ): EventTypeInputDelayEventTypeMap | null => {
        const selNodeEvList = filterOnContextIncludeParent(selNode, evList);
        const toDelayEvList = selNodeEvList.filter((o) => o.eventTypeId === evType.eventTypeId);
        let earliestToDelayDate: Date;
        if (toDelayEvList && toDelayEvList.length > 0) {
            const earliestToEv = findEarliestEvent(toDelayEvList);
            earliestToDelayDate = earliestToEv.eventDate.actualDateTimeLocal;
        } else {
            earliestToDelayDate = evDateTimeLocal;
        }
        earliestToDelayDate = evDateTimeLocal;
        const fromDelayEvList = evList.filter((o) => o.eventTypeId === inpDelay.startDelayEventTypeId);
        if (fromDelayEvList && fromDelayEvList.length > 0) {
            let [latestFromEvList, latestFromDate]: [TankersEvent[], Date | null] = [[], null];
            nodeList.forEach((node) => {
                if (node) {
                    const nodeEvList = filterOnContextIncludeParent(node, evList);
                    const [nodeLatestFromEvList, nodeLatestFromDate] = findLatestFromEventList(
                        nodeEvList,
                        inpDelay,
                        earliestToDelayDate,
                        evType,
                        evDateTimeLocal,
                        existingEvent
                    );

                    if (nodeLatestFromDate) {
                        if (!latestFromDate) {
                            latestFromDate = nodeLatestFromDate;
                            latestFromEvList = nodeLatestFromEvList;
                        } else {
                            if (isSameDate(latestFromDate, nodeLatestFromDate)) {
                                latestFromEvList = latestFromEvList.concat(nodeLatestFromEvList);
                            } else if (isDateAfter(getDateWithoutSecMs(nodeLatestFromDate), latestFromDate)) {
                                latestFromDate = nodeLatestFromDate;
                                latestFromEvList = nodeLatestFromEvList;
                            }
                        }
                    }
                }
            });
            if (existingEvent) {
                const earliestEv = toDelayEvList.find((o) => o.eventId === existingEvent.eventId);
                if (!earliestEv) {
                    return null;
                }
                if (isDateBefore(getDateWithoutSecMs(earliestToDelayDate), latestFromDate)) {
                    return null;
                }
            } else {
                if (isDateBefore(getDateWithoutSecMs(earliestToDelayDate), latestFromDate)) {
                    return null;
                }
            }
            return inpDelay;
        }
        return null;
    };

    const isDelayInput = (evType: EventType, evTypeInput: EventTypeInput): boolean => {
        if (evType.eventTypeInputDelayEventTypeMapArray && evType.eventTypeInputDelayEventTypeMapArray.length > 0) {
            return Boolean(evType.eventTypeInputDelayEventTypeMapArray.find((o) => o.eventTypeInputId === evTypeInput.eventTypeInputId));
        }
        return false;
    };

    // Check input item, for validation of input item, true = valid, false = not valid. Not valid, if:
    //      eventTypeInputDelayEventTypeMapArray is not empty, and
    //      input item has entry in eventTypeInputDelayEventTypeMapArray, and
    //      there are events in the port call, and
    //      there are start-delay events in the port call, and
    //      delay limit is exceeded
    //      otherwise: valid is true
    const hasNoDelayOrNotExceededLimits = (
        rot: Rotation,
        evType: EventType,
        evTypeInput: EventTypeInput | null,
        evDateTimeLocal: Date | null,
        evList: TankersEvent[],
        ev: TankersEvent | null,
        node: IRotationNode | null
    ): boolean => {
        if (evTypeInput && evDateTimeLocal && evType.eventTypeInputDelayEventTypeMapArray && evType.eventTypeInputDelayEventTypeMapArray.length > 0) {
            if (evList && evList.length > 0) {
                if (isDelayExceeded(rot, evType, evList, evTypeInput, evDateTimeLocal, ev, node)) return false;
            }
        }
        return true;
    };

    const hasDelayInputItemReg = (evTypeInp: EventTypeInput, ev: TankersEvent | null, evType: EventType | null): boolean => {
        if (ev && ev.eventTypeInputRegList && ev.eventTypeInputRegList.length > 0) {
            const delayInpList = ev.eventType?.eventTypeInputDelayEventTypeMapArray.filter((o) => {
                return o.eventTypeInputId === evTypeInp.eventTypeInputId;
            });
            const evInpReg = ev.eventTypeInputRegList.find((o) => {
                return Boolean(delayInpList?.find((p) => p.eventTypeInputId === o.eventTypeInputId));
            });
            return Boolean(evInpReg);
        } else if (evType && evType.eventTypeInputArray) {
            const inp = evType.eventTypeInputArray.find((o) => o.eventTypeInputId === evTypeInp.eventTypeInputId);
            if (inp && inp.eventTypeInputReg && inp.eventTypeInputReg) {
                const delayInpList = evType.eventTypeInputDelayEventTypeMapArray.filter((o) => {
                    return o.eventTypeInputId === inp.eventTypeInputId;
                });
                const evInpReg = delayInpList?.find((p) => p.eventTypeInputId === inp.eventTypeInputId);
                return Boolean(evInpReg);
            }
        }
        return false;
    };

    const eventHasDelayAndMissingInput = (rot: Rotation | null, ev: TankersEvent, evList: TankersEvent[], node: IRotationNode | null): boolean => {
        if (rot && ev.eventType && ev.eventType.eventTypeInputArray && ev.eventType.eventTypeInputArray.length > 0) {
            const evInpDelayExceeded = ev.eventType?.eventTypeInputArray?.find((evInp) => {
                if (hasNoDelayOrNotExceededLimits(rot, ev.eventType, evInp, ev.eventDate.actualDateTimeLocal, evList, ev, node)) return false;
                return !hasDelayInputItemReg(evInp, ev, null);
            });
            return Boolean(evInpDelayExceeded);
        }
        return false;
    };

    const eventHasNoDelayAndExistingInput = (rot: Rotation | null, ev: TankersEvent, evList: TankersEvent[], node: IRotationNode | null): boolean => {
        if (rot && ev.eventType && ev.eventType.eventTypeInputArray && ev.eventType.eventTypeInputArray.length > 0) {
            const evInpNoDelayAndInput = ev.eventType?.eventTypeInputArray?.find((evInp) => {
                if (hasDelayInputItemReg(evInp, ev, null))
                    return hasNoDelayOrNotExceededLimits(rot, ev.eventType, evInp, ev.eventDate.actualDateTimeLocal, evList, ev, node);
                return false;
            });
            return Boolean(evInpNoDelayAndInput);
        }
        return false;
    };

    const eventHasDelayAndExistingInput = (ev: TankersEvent): boolean => {
        if (ev.eventType && ev.eventType.eventTypeInputArray && ev.eventType.eventTypeInputArray.length > 0) {
            const hasInpDelayReg = ev.eventType?.eventTypeInputArray?.find((evInp) => {
                return hasDelayInputItemReg(evInp, ev, null);
            });
            return Boolean(hasInpDelayReg);
        }
        return false;
    };

    const nodeHasDelayAndMissingInput = (
        rot: Rotation,
        evListForNode: TankersEvent[],
        evList: TankersEvent[],
        node: IRotationNode | null
    ): boolean => {
        const delayFound = evListForNode.find((evDelay) => {
            return eventHasDelayAndMissingInput(rot, evDelay, evList, node);
        });
        return Boolean(delayFound);
    };

    const nodeHasNotDelayAndExistingInput = (
        rot: Rotation,
        evListForNode: TankersEvent[],
        evList: TankersEvent[],
        node: IRotationNode | null
    ): boolean => {
        const delayFound = evListForNode.find((evDelay) => {
            return eventHasNoDelayAndExistingInput(rot, evDelay, evList, node);
        });
        return Boolean(delayFound);
    };

    const updateDeltaEventArray = (tankersEventDataBM: TankersEventDataBM, offlineReportEvents: any[]) => {
        if (tankersEventDataBM && tankersEventDataBM.events && tankersEventDataBM.events.length > 0) {
            const evList = newEventList(tankersEventDataBM, offlineReportEvents, false);
            if (evList) {
                setDeltaEventArray(evList);
            }
        }
    };

    const addEventToList = (ev: TankersEvent) => {
        const evList: TankersEvent[] = [];
        evList.push(ev);
        setDeltaEventArray(evList);
    };

    const updateEventInList = (ev: TankersEvent) => {
        const evList: TankersEvent[] = [];
        evList.push(ev);
        setDeltaEventArray(evList);
    };

    const deleteEventInList = (eventId: string) => {
        const deletedEvent = fullEventArray.find((evt) => evt.eventId === eventId);
        if (deletedEvent) {
            deletedEvent.deleted = true;
            const evList: TankersEvent[] = [];
            evList.push(deletedEvent);
            setDeltaEventArray(evList);
        }
    };

    const deleteEventsForPortCallInList = (portCallId: number, allBerthCalls: boolean) => {
        const evDelList = fullEventArray.filter((evt) => evt.portCallId === portCallId && (allBerthCalls || !evt.berthCallId));
        if (evDelList && evDelList.length > 0) {
            evDelList.forEach((ev) => (ev.deleted = true));
            setDeltaEventArray(evDelList);
        }
    };

    const deleteEventsForBerthCallInList = (portCallId: number, berthCallId: number, allCargoOps: boolean) => {
        const evDelList = fullEventArray.filter(
            (evt) => evt.portCallId === portCallId && evt.berthCallId === berthCallId && (allCargoOps || !evt.cargoOperationId)
        );
        if (evDelList && evDelList.length > 0) {
            evDelList.forEach((ev) => (ev.deleted = true));
            setDeltaEventArray(evDelList);
        }
    };

    const deleteEventsForCargoOperationInList = (portCallId: number, berthCallId: number, cargoOperationId: number) => {
        const evDelList = fullEventArray.filter(
            (evt) => evt.portCallId === portCallId && evt.berthCallId === berthCallId && evt.cargoOperationId === cargoOperationId
        );
        if (evDelList && evDelList.length > 0) {
            evDelList.forEach((ev) => (ev.deleted = true));
            setDeltaEventArray(evDelList);
        }
    };

    const compareActualUtcEventDateTime = (a: TankersEvent, b: TankersEvent, desc: boolean): number => {
        let cmp = 0;
        if (isDateAfter(a.eventDate?.actualDateTimeUtc, b.eventDate?.actualDateTimeUtc)) {
            cmp = 1;
        } else if (isDateBefore(a.eventDate?.actualDateTimeUtc, b.eventDate?.actualDateTimeUtc)) {
            cmp = -1;
        }
        return desc ? cmp * -1 : cmp;
    };

    const moveEventsToCargoOperationUpdate = (moveEvCo: MoveEventsToCargoOperationBM) => {
        const eventsForBerthCallFromList = fullEventArray
            .filter(
                (ev) =>
                    !ev.deleted &&
                    ev.portCallId === moveEvCo.portCallId &&
                    ev.berthCallId &&
                    ev.berthCallId === moveEvCo.fromBerthCallId &&
                    !ev.cargoOperationId
            )
            .sort((a, b) => compareActualUtcEventDateTime(a, b, false));

        const eventsForBerthCallToList = fullEventArray
            .filter(
                (ev) =>
                    !ev.deleted &&
                    ev.portCallId === moveEvCo.portCallId &&
                    ev.berthCallId &&
                    ev.berthCallId === moveEvCo.toBerthCallId &&
                    !ev.cargoOperationId
            )
            .sort((a, b) => compareActualUtcEventDateTime(a, b, false));

        const eventsForCargoOpList = fullEventArray.filter(
            (ev) =>
                !ev.deleted &&
                ev.portCallId === moveEvCo.portCallId &&
                ev.berthCallId &&
                ev.berthCallId === moveEvCo.fromBerthCallId &&
                ev.cargoOperationId &&
                ev.cargoOperationId === moveEvCo.cargoOperationId
        );

        const upd = getUtcTimeNow();
        eventsForCargoOpList.forEach((ev) => {
            if (ev.eventTypeGroupSequenceDependencyIdentity) {
                const fromBerthEv = eventsForBerthCallFromList.find(
                    (o) => o.eventTypeGroupSequenceIdentity === ev.eventTypeGroupSequenceDependencyIdentity
                );
                if (fromBerthEv) {
                    const toBerthEv = eventsForBerthCallToList.find((o) => o.eventTypeId === fromBerthEv?.eventTypeId);
                    if (toBerthEv) {
                        ev.eventTypeGroupSequenceDependencyIdentity = toBerthEv.eventTypeGroupSequenceIdentity;
                    }
                }
            }
            ev.berthCallId = moveEvCo.toBerthCallId;
            ev.berthId = moveEvCo.toBerthId;
            ev.lastUpdated = upd;
            ev.updatedBy = moveEvCo.user;
        });
        setDeltaEventArray(eventsForCargoOpList);
    };

    const moveEventsToBerthCallUpdate = (moveEvBc: MoveEventsToBerthCallBM) => {
        const eventsForBerthCallList = fullEventArray.filter(
            (ev) => !ev.deleted && ev.portCallId === moveEvBc.portCallId && ev.berthCallId === moveEvBc.fromBerthCallId
        );

        const upd = getUtcTimeNow();
        eventsForBerthCallList.forEach((ev) => {
            ev.berthCallId = moveEvBc.toBerthCallId;
            ev.berthId = moveEvBc.toBerthId;
            ev.berthSequenceNumber = moveEvBc.toBerthSequenceNumber;
            ev.lastUpdated = upd;
            ev.updatedBy = moveEvBc.user;
        });
        setDeltaEventArray(eventsForBerthCallList);
    };

    const moveEventsToPortCallUpdate = (moveEvPc: MoveEventsPortCallBM) => {
        //const newEvList = createNewEventList(fullEventArray);
        const eventsForPortCallList = fullEventArray.filter((ev) => !ev.deleted && ev.portCallId === moveEvPc.fromPortCallId);
        const upd = getUtcTimeNow();
        eventsForPortCallList.forEach((ev) => {
            ev.portCallId = moveEvPc.toPortCallId;
            ev.voyageNumber = moveEvPc.toVoyageNumber;
            ev.lastUpdated = upd;
            ev.updatedBy = moveEvPc.user;
            if (ev.berthCallId) {
                const moveBc = moveEvPc.moveEventsBerthCallList.find((o) => o.fromBerthCallId === ev.berthCallId);
                if (moveBc) {
                    ev.berthCallId = moveBc.toBerthCallId;
                    if (ev.cargoOperationId) {
                        const moveCo = moveBc.moveEventsCargoOperationList.find((o) => o.fromCargoOperationId === ev.cargoOperationId);
                        if (moveCo) {
                            ev.cargoOperationId = moveCo.toCargoOperationId;
                        }
                    }
                }
            }
        });
        setDeltaEventArray(eventsForPortCallList);
    };

    const filterEventsOnActual = (eventArray: TankersEvent[]): TankersEvent[] => {
        return eventArray.filter((e: TankersEvent) => e.isActual());
    };

    const filterEventsOnDeleted = (eventArray: TankersEvent[]): TankersEvent[] => {
        return eventArray.filter((e: TankersEvent) => !e.deleted);
    };

    const findLatestEventWithPortCall = (rot: Rotation, evList: TankersEvent[]): number => {
        return evList.findIndex((o) => rot.portCallArray.find((pc) => !pc.isDeleted && pc.portCallId === o.portCallId) !== undefined);
    };

    const getCurrentPortCall = (rotation: Rotation): PortCall | null => {
        if (rotation && rotation.portCallArray.length > 0) {
            //TODO: LOGIC-ERROR - make this work when fullEventArray is empty, should still get current port call?
            if (fullEventArray && fullEventArray.length > 0) {
                const eospSospList = fullEventArray
                    .filter((ev) => ev.eventTypeId === EOSP || ev.eventTypeId === SOSP)
                    .sort((a, b) => compareActualEventDateTime(a, b, true));
                const ind = findLatestEventWithPortCall(rotation, eospSospList);
                if (ind >= 0) {
                    if (eospSospList[ind].eventTypeId === EOSP) {
                        const eospPortCall = rotation.portCallArray.find(
                            (pc) => pc.portCallId === eospSospList[ind].portCallId && pc.reportLocation === ReportLocationEnum.Port
                        );
                        if (eospPortCall) {
                            return eospPortCall;
                        }
                    } else {
                        const portCallIndex = rotation.portCallArray.findIndex(
                            (pc) => pc.portCallId === eospSospList[ind].portCallId && pc.reportLocation === ReportLocationEnum.Sea
                        );

                        if (portCallIndex >= 0) {
                            if (portCallIndex === rotation.portCallArray.length - 1) {
                                return rotation.portCallArray[rotation.portCallArray.length - 1];
                            }
                            return rotation.portCallArray[portCallIndex];
                        }
                    }
                }
            }
            return rotation.portCallArray[rotation.portCallArray.length - 1];
        }
        return null;
    };

    const getCurrentVoyage = (): string | null => {
        if (updatedRotation && updatedRotation.portCallArray.length > 0) {
            if (fullEventArray && fullEventArray.length > 0) {
                const eospSospList = fullEventArray
                    .filter((ev) => ev.eventTypeId === EOSP || ev.eventTypeId === SOSP)
                    .sort((a, b) => compareActualEventDateTime(a, b, true));
                const ind = findLatestEventWithPortCall(updatedRotation, eospSospList);
                if (ind >= 0) {
                    if (eospSospList[ind].eventTypeId === EOSP) {
                        const eospPortCall = updatedRotation.portCallArray.find((pc) => pc.portCallId === eospSospList[ind].portCallId);
                        if (eospPortCall) {
                            return eospPortCall.voyageNumber1;
                        }
                    } else {
                        const portCallIndex = updatedRotation.portCallArray.findIndex((pc) => pc.portCallId === eospSospList[ind].portCallId);
                        if (portCallIndex >= 0) {
                            if (portCallIndex === updatedRotation.portCallArray.length - 1) {
                                return updatedRotation.portCallArray[updatedRotation.portCallArray.length - 1].voyageNumber1;
                            }
                            return updatedRotation.portCallArray[portCallIndex + 1].voyageNumber1;
                        }
                    }
                }
            }
            return updatedRotation.portCallArray[0].voyageNumber1;
        }
        return null;
    };

    const getHistoricRotationTree = (): Rotation | null => {
        if (updatedRotation) {
            const newRotation = newRotationFromCode(updatedRotation);
            const currentPortCall = getCurrentPortCall(newRotation);
            const currentPortCallIndex = newRotation.portCallArray.findIndex((portCall) => {
                return portCall.portCallId === currentPortCall?.portCallId && portCall.reportLocation === currentPortCall?.reportLocation;
            });
            const processedPortCallsArray = newRotation.portCallArray.slice(currentPortCallIndex - 10, currentPortCallIndex);
            if (processedPortCallsArray) {
                newRotation.portCallArray = processedPortCallsArray;
            }
            return newRotation;
        }
        return null;
    };

    const getRotationTreeByVoyages = (voyageNumbers: string[], rot: Rotation | null): Rotation | null => {
        if (rot) {
            const newRotation = newRotationFromCode(rot);
            if (newRotation.portCallArray) {
                newRotation.portCallArray = newRotation?.portCallArray?.filter((portCall) => {
                    return voyageNumbers.some(
                        (voyageNumber) => portCall.voyageNumber1.includes(voyageNumber) || portCall.voyageNumber2?.includes(voyageNumber)
                    );
                });
            }
            return newRotation;
        }
        return null;
    };

    const getDefaultRotationTree = (rot: Rotation | null): Rotation | null => {
        if (rot) {
            const newRotation = newRotationFromCode(rot);
            const currentPortCall = getCurrentPortCall(newRotation);
            const currentPortCallIndex = newRotation.portCallArray.findIndex((portCall) => {
                return portCall.portCallId === currentPortCall?.portCallId && portCall.reportLocation === currentPortCall?.reportLocation;
            });
            const processedPortCallsArray = newRotation.portCallArray.slice(currentPortCallIndex - 2, currentPortCallIndex + 3);
            if (processedPortCallsArray) {
                newRotation.portCallArray = processedPortCallsArray;
            }
            return newRotation;
        }
        return null;
    };

    const isAtSea = (selectedPortCall: PortCall | null): boolean => {
        if (selectedPortCall && fullEventArray && fullEventArray.length > 0) {
            const eosp = fullEventArray.find((ev) => ev.portCallId === selectedPortCall.portCallId && ev.eventTypeId === EOSP);
            if (eosp) {
                const sosp = fullEventArray.find((ev) => ev.portCallId === selectedPortCall.portCallId && ev.eventTypeId === SOSP);
                if (!sosp) {
                    return false;
                }
            }
        }
        return true;
    };

    const addCargoEvents = (rotationNode: IRotationNode, eventArray: TankersEvent[]): TankersEvent[] => {
        let filteredEventList: TankersEvent[] = [];
        if (rotationNode) {
            switch (rotationNode.type) {
                case RotationTreeEnum.Sea:
                case RotationTreeEnum.PortCall:
                    filteredEventList = filterOnContext(rotationNode, eventArray);
                    break;
                case RotationTreeEnum.BerthCall: {
                    filteredEventList = filterOnContext(rotationNode, eventArray);
                    const portCall = getParentPortCallFromNode(rotationNode);
                    if (portCall) {
                        const berthCall: BerthCall = rotationNode as BerthCall;
                        if (berthCall.cargoOperationArray.length > 0) {
                            filteredEventList = filteredEventList.concat(
                                getCargoEventsForPort(filterOnContextSingleLevel(portCall, eventArray), berthCall.cargoOperationArray)
                            );
                        }
                    }
                    break;
                }
                case RotationTreeEnum.CargoOperation: {
                    filteredEventList = filterOnContext(rotationNode, eventArray);
                    const berthCall2 = getParentBerthCallFromNode(rotationNode);
                    if (berthCall2) {
                        filteredEventList = filteredEventList.concat(
                            getCargoEvents(filterOnContextSingleLevel(berthCall2, eventArray), rotationNode.id)
                        );
                        const portCall2 = getParentPortCallFromNode(berthCall2);
                        if (portCall2) {
                            filteredEventList = filteredEventList.concat(
                                getCargoEvents(filterOnContextSingleLevel(portCall2, eventArray), rotationNode.id)
                            );
                        }
                    }
                    break;
                }
            }
        }
        return filteredEventList.sort((a, b) => compareActualEventDateTime(a, b, false));
    };

    const filterSameEventTypeGroupAndLevel = (eventArray: TankersEvent[], evType: EventType): TankersEvent[] => {
        return eventArray.filter(
            (ev) =>
                ev.eventType &&
                ev.eventType?.eventTypeGroupId === evType.eventTypeGroupId &&
                ev.eventType?.eventTypeGroup?.eventTypeLevel?.eventTypeLevelId === evType.eventTypeGroup?.eventTypeLevel?.eventTypeLevelId
        );
    };

    const filterCargoOperationEvents = (eventArray: TankersEvent[], cargoOp: CargoOperation): TankersEvent[] => {
        let filteredEventList: TankersEvent[] = [];
        const berthCall = getParentBerthCallFromNode(cargoOp);
        if (berthCall) {
            const portCall = getParentPortCallFromNode(berthCall);
            if (portCall) {
                filteredEventList = eventArray.filter((e) => {
                    return (
                        e.portCallId &&
                        e.portCallId === portCall.portCallId &&
                        e.berthCallId &&
                        e.berthCallId === berthCall.berthCallId &&
                        e.cargoOperationId &&
                        e.cargoOperationId > 0 &&
                        e.cargoOperationId === cargoOp.cargoOperationId
                    );
                });
            }
        }
        return filteredEventList;
    };

    const filterOnContextSingleLevelIncludingCargoEvents = (rotationNode: IRotationNode, eventArray: TankersEvent[]): TankersEvent[] => {
        let filteredEventList: TankersEvent[] = [];
        if (rotationNode && rotationNode.portCall) {
            switch (rotationNode.type) {
                case RotationTreeEnum.Sea:
                    filteredEventList = eventArray.filter((ev) => {
                        return ev.portCallId === rotationNode.portCall?.id && !ev.berthCallId && ev.eventTypeContextId === ReportLocationEnum.Sea;
                    });
                    break;
                case RotationTreeEnum.PortCall:
                    filteredEventList = eventArray.filter((ev) => {
                        return ev.portCallId === rotationNode.portCall?.id && !ev.berthCallId && ev.eventTypeContextId === ReportLocationEnum.Port;
                    });
                    break;
                case RotationTreeEnum.BerthCall: {
                    const berthCall = getBerthCallFromNode(rotationNode);
                    if (berthCall) {
                        const portCall = getParentPortCallFromNode(rotationNode);
                        if (portCall) {
                            filteredEventList = eventArray.filter((e) => {
                                return e.portCallId === portCall.portCallId && e.berthCallId === berthCall.berthCallId && !e.cargoOperationId;
                            });
                            const berthCall3: BerthCall = rotationNode as BerthCall;
                            if (berthCall3.cargoOperationArray.length > 0) {
                                filteredEventList = filteredEventList.concat(
                                    getCargoEventsForPort(filterOnContextSingleLevel(portCall, eventArray), berthCall3.cargoOperationArray)
                                );
                            }
                        }
                    }
                    break;
                }
                case RotationTreeEnum.CargoOperation: {
                    const cargoOperation = getCargoOperationFromNode(rotationNode);
                    if (cargoOperation) {
                        filteredEventList = filterCargoOperationEvents(eventArray, cargoOperation);
                        const berthCall2 = getParentBerthCallFromNode(rotationNode);
                        if (berthCall2) {
                            if (!cargoOperation.isMoved && !cargoOperation.isDeleted) {
                                filteredEventList = filteredEventList.concat(
                                    getCargoEvents(filterOnContextSingleLevel(berthCall2, eventArray), rotationNode.id)
                                );
                            }
                            const portCall = getParentPortCallFromNode(berthCall2);
                            if (portCall) {
                                if (!cargoOperation.isMoved && !cargoOperation.isDeleted) {
                                    filteredEventList = filteredEventList.concat(
                                        getCargoEvents(filterOnContextSingleLevel(portCall, eventArray), rotationNode.id)
                                    );
                                }
                            }
                        }
                    }
                    break;
                }
            }
        }
        return filteredEventList;
    };

    const filterOnLocationContext = (eventArray: TankersEvent[], reportLocation: ReportLocationEnum) => {
        return eventArray.filter((ev) => ev.eventTypeContextId === reportLocation);
    };

    const filterOnContextSingleLevel = (rotationNode: IRotationNode, eventArray: TankersEvent[]): TankersEvent[] => {
        let filteredEventList: TankersEvent[] = [];
        if (rotationNode && rotationNode.portCall) {
            switch (rotationNode.type) {
                case RotationTreeEnum.Sea:
                    filteredEventList = eventArray.filter(
                        (ev) => ev.portCallId === rotationNode.portCall?.id && !ev.berthCallId && ev.eventTypeContextId === ReportLocationEnum.Sea
                    );
                    break;
                case RotationTreeEnum.PortCall:
                    filteredEventList = eventArray.filter(
                        (ev) => ev.portCallId === rotationNode.portCall?.id && !ev.berthCallId && ev.eventTypeContextId === ReportLocationEnum.Port
                    );
                    break;
                case RotationTreeEnum.BerthCall: {
                    const berthCall = getBerthCallFromNode(rotationNode);
                    if (berthCall) {
                        const portCall = getParentPortCallFromNode(berthCall);
                        if (portCall) {
                            filteredEventList = eventArray.filter(
                                (e) => e.portCallId === portCall.portCallId && e.berthCallId === berthCall.berthCallId && !e.cargoOperationId
                            );
                        }
                    }
                    break;
                }
                case RotationTreeEnum.CargoOperation: {
                    const cargoOperation = getCargoOperationFromNode(rotationNode);
                    if (cargoOperation) {
                        filteredEventList = filterCargoOperationEvents(eventArray, cargoOperation);
                    }
                    break;
                }
            }
        }
        return filteredEventList;
    };

    const filterOnContextSingleLevelIncludeParents = (rotationNode: IRotationNode, eventArray: TankersEvent[]): TankersEvent[] => {
        let filteredEventList: TankersEvent[] = [];
        if (rotationNode && rotationNode.portCall) {
            switch (rotationNode.type) {
                case RotationTreeEnum.Sea:
                    filteredEventList = eventArray.filter((ev) => {
                        return ev.portCallId === rotationNode.portCall?.id && !ev.berthCallId && ev.eventTypeContextId === ReportLocationEnum.Sea;
                    });
                    break;
                case RotationTreeEnum.PortCall:
                    filteredEventList = eventArray.filter((ev) => {
                        return ev.portCallId === rotationNode.portCall?.id && !ev.berthCallId && ev.eventTypeContextId === ReportLocationEnum.Port;
                    });
                    break;
                case RotationTreeEnum.BerthCall: {
                    const berthCall = getBerthCallFromNode(rotationNode);
                    const portCall = getParentPortCallFromNode(berthCall);
                    if (portCall) {
                        if (berthCall) {
                            filteredEventList = eventArray.filter((e) => {
                                return e.portCallId === portCall.portCallId && e.berthCallId === berthCall.berthCallId && !e.cargoOperationId;
                            });
                            const portCallEvList = eventArray.filter((e) => e.portCallId === portCall.id && !e.berthCallId);
                            filteredEventList = filteredEventList.concat(portCallEvList);
                        }
                    }
                    break;
                }
                case RotationTreeEnum.CargoOperation: {
                    const cargoOperation = getCargoOperationFromNode(rotationNode);
                    if (cargoOperation) {
                        filteredEventList = filterCargoOperationEvents(eventArray, cargoOperation);
                        const berthCall2 = getParentBerthCallFromNode(cargoOperation);
                        if (berthCall2) {
                            const portCall2 = getParentPortCallFromNode(berthCall2);
                            if (portCall2) {
                                const berthCallEvList = eventArray.filter(
                                    (e) => e.portCallId === portCall2.portCallId && e.berthCallId === berthCall2.berthCallId && !e.cargoOperationId
                                );
                                filteredEventList = filteredEventList.concat(berthCallEvList);
                                const portCallEvList2 = eventArray.filter((e) => e.portCallId === portCall2.id && !e.berthCallId);
                                filteredEventList = filteredEventList.concat(portCallEvList2);
                            }
                        }
                    }
                    break;
                }
            }
        }
        return filteredEventList;
    };

    const filterOnContext = (rotationNode: IRotationNode, eventArray: TankersEvent[]): TankersEvent[] => {
        let filteredEventList: TankersEvent[] = [];
        const portCall = getPortCallFromNode(rotationNode);
        if (rotationNode && portCall) {
            switch (rotationNode.type) {
                case RotationTreeEnum.Sea:
                    filteredEventList = eventArray.filter((e) => {
                        return e.portCallId === portCall.id && e.eventTypeContextId === ReportLocationEnum.Sea;
                    });
                    break;
                case RotationTreeEnum.PortCall:
                    filteredEventList = eventArray.filter((e) => {
                        return e.portCallId === portCall.id && e.eventTypeContextId === ReportLocationEnum.Port;
                    });
                    break;
                case RotationTreeEnum.BerthCall: {
                    const berthCall = getBerthCallFromNode(rotationNode);
                    if (berthCall) {
                        const portCall = getPortCallFromNode(berthCall);
                        if (portCall) {
                            filteredEventList = eventArray.filter((e) => {
                                return e.portCallId === portCall.portCallId && e.berthCallId === berthCall.berthCallId;
                            });
                        }
                    }
                    break;
                }
                case RotationTreeEnum.CargoOperation: {
                    const cargoOperation = getCargoOperationFromNode(rotationNode);
                    if (cargoOperation) {
                        filteredEventList = filterCargoOperationEvents(eventArray, cargoOperation);
                    }
                    break;
                }
            }
        }
        return filteredEventList;
    };

    const filterOnContextSeaIncludePort = (rotationNode: IRotationNode, eventArray: TankersEvent[]): TankersEvent[] => {
        let filteredEventList: TankersEvent[] = [];
        if (rotationNode && rotationNode.portCall) {
            switch (rotationNode.type) {
                case RotationTreeEnum.Sea:
                    filteredEventList = eventArray.filter((e) => {
                        return entityOk(e, rotationNode, false);
                    });
                    break;
                case RotationTreeEnum.PortCall:
                    filteredEventList = eventArray.filter((e) => {
                        return e.portCallId === rotationNode.portCall?.id && e.eventTypeContextId === ReportLocationEnum.Port;
                    });
                    break;
                case RotationTreeEnum.BerthCall: {
                    const berthCall = getBerthCallFromNode(rotationNode);
                    if (berthCall) {
                        const portCall = getPortCallFromNode(berthCall);
                        if (portCall) {
                            filteredEventList = eventArray.filter((e) => {
                                return e.portCallId === portCall.portCallId && e.berthCallId === berthCall.berthCallId;
                            });
                        }
                    }
                    break;
                }
                case RotationTreeEnum.CargoOperation: {
                    const cargoOperation = getCargoOperationFromNode(rotationNode);
                    if (cargoOperation) {
                        filteredEventList = filterCargoOperationEvents(eventArray, cargoOperation);
                    }
                    break;
                }
            }
        }
        return filteredEventList;
    };

    const filterOnContextIncludeParent = (rotationNode: IRotationNode, eventArray: TankersEvent[]): TankersEvent[] => {
        let filteredEventList: TankersEvent[] = [];
        if (rotationNode && rotationNode.portCall) {
            switch (rotationNode.type) {
                case RotationTreeEnum.Sea:
                    filteredEventList = eventArray.filter(
                        (ev) => ev.portCallId === rotationNode.portCall?.id && ev.eventTypeContextId === ReportLocationEnum.Sea
                    );
                    break;
                case RotationTreeEnum.PortCall:
                    filteredEventList = eventArray.filter(
                        (ev) => ev.portCallId === rotationNode.portCall?.id && ev.eventTypeContextId === ReportLocationEnum.Port
                    );
                    break;
                case RotationTreeEnum.BerthCall: {
                    const berthCall = getBerthCallFromNode(rotationNode);
                    if (berthCall) {
                        const portCall = getParentPortCallFromNode(berthCall);
                        if (portCall) {
                            filteredEventList = eventArray.filter(
                                (e) => e.portCallId === portCall.portCallId && e.berthCallId === berthCall.berthCallId
                            );
                            const portCallEvList = eventArray.filter((e) => e.portCallId === portCall.id && !e.berthCallId);
                            filteredEventList = filteredEventList.concat(portCallEvList);
                        }
                    }
                    break;
                }
                case RotationTreeEnum.CargoOperation: {
                    const cargoOperation = getCargoOperationFromNode(rotationNode);
                    if (cargoOperation) {
                        filteredEventList = filterCargoOperationEvents(eventArray, cargoOperation);
                        const berthCall2 = getParentBerthCallFromNode(cargoOperation);
                        if (berthCall2) {
                            const portCall2 = getParentPortCallFromNode(berthCall2);
                            if (portCall2) {
                                const berthCallEvList = eventArray.filter(
                                    (e) => e.portCallId === portCall2.portCallId && e.berthCallId === berthCall2.berthCallId && !e.cargoOperationId
                                );
                                filteredEventList = filteredEventList.concat(berthCallEvList);
                                const portCallEvList = eventArray.filter((e) => e.portCallId === portCall2.id && !e.berthCallId);
                                filteredEventList = filteredEventList.concat(portCallEvList);
                            }
                        }
                    }
                    break;
                }
            }
        }
        return filteredEventList;
    };

    const filterOnContextIncludeParentWithBerthCall = (rotationNode: IRotationNode, eventArray: TankersEvent[]): TankersEvent[] => {
        let filteredEventList: TankersEvent[] = [];
        if (rotationNode && rotationNode.portCall) {
            let rotNode = rotationNode;
            if (rotationNode.type === RotationTreeEnum.CargoOperation) {
                rotNode = getBerthCallFromNode(rotationNode) as IRotationNode;
            }
            switch (rotNode.type) {
                case RotationTreeEnum.Sea:
                    filteredEventList = eventArray?.filter(
                        (ev) => ev.portCallId === rotNode.portCall?.id && ev.eventTypeContextId === ReportLocationEnum.Sea
                    );
                    break;
                case RotationTreeEnum.PortCall:
                    filteredEventList = eventArray?.filter(
                        (ev) => ev.portCallId === rotNode.portCall?.id && ev.eventTypeContextId === ReportLocationEnum.Port
                    );
                    break;
                case RotationTreeEnum.BerthCall: {
                    const berthCall = getBerthCallFromNode(rotNode);
                    if (berthCall) {
                        const portCall = getParentPortCallFromNode(berthCall);
                        if (portCall) {
                            filteredEventList = eventArray?.filter(
                                (e) => e.portCallId === portCall.portCallId && e.berthCallId === berthCall.berthCallId
                            );
                            const portCallEvList = eventArray?.filter((e) => e.portCallId === portCall.id && !e.berthCallId);
                            filteredEventList = filteredEventList?.concat(portCallEvList);
                        }
                    }
                    break;
                }
                case RotationTreeEnum.CargoOperation: {
                    const cargoOperation = getCargoOperationFromNode(rotNode);
                    if (cargoOperation) {
                        filteredEventList = filterCargoOperationEvents(eventArray, cargoOperation);
                        const berthCall2 = getParentBerthCallFromNode(cargoOperation);
                        if (berthCall2) {
                            const portCall2 = getParentPortCallFromNode(berthCall2);
                            if (portCall2) {
                                const berthCallEvList = eventArray?.filter(
                                    (e) => e.portCallId === portCall2.portCallId && e.berthCallId === berthCall2.berthCallId && !e.cargoOperationId
                                );
                                filteredEventList = filteredEventList.concat(berthCallEvList);
                                const portCallEvList = eventArray?.filter((e) => e.portCallId === portCall2.id && !e.berthCallId);
                                filteredEventList = filteredEventList.concat(portCallEvList);
                            }
                        }
                    }
                    break;
                }
            }
        }
        return filteredEventList;
    };

    const getFullEventArray = (): TankersEvent[] => {
        return fullEventArray;
    };

    const isCargoDropdown = (dropdownTypeId: DropdownTypeEnum): boolean => {
        return (
            dropdownTypeId === DropdownTypeEnum.CargoLoadingAndUnloadingOperations ||
            dropdownTypeId === DropdownTypeEnum.CargoLoadingOperations ||
            dropdownTypeId === DropdownTypeEnum.CargoUnloadingOperations
        );
    };

    const isBerthDropdown = (dropdownTypeId: DropdownTypeEnum) => dropdownTypeId === DropdownTypeEnum.BerthList;

    const getCargoEvents = (eventArray: TankersEvent[], cargoOperationId: number): TankersEvent[] => {
        const evArray = eventArray?.filter((ev) => {
            const eventTypeInputDropdownArray = ev.eventTypeInputRegList?.filter((drdownGr) => {
                return drdownGr.dropdownGroupReg;
            });

            const evInpGrArray = eventTypeInputDropdownArray.filter((evInpGr) => {
                return isCargoDropdown(evInpGr.dropdownGroupReg?.dropdownTypeId);
            });
            if (evInpGrArray && evInpGrArray.length > 0) {
                const dropdownGroupRegList = evInpGrArray.flatMap((eigr) => eigr.dropdownGroupReg);

                const evInpRegArray = dropdownGroupRegList.flatMap((ddgr) => ddgr && ddgr.dropdownItemRegList);

                const evInpReg = evInpRegArray.find((inpReg) => {
                    return inpReg && inpReg.dropdownItemId === cargoOperationId;
                });
                return evInpReg;
            } else {
                return false;
            }
        });
        // Find events for all event types in event type group sequences found above
        let evArrayForAllEventTypes: TankersEvent[] = [];
        evArray.forEach((ev) => {
            const evArr = eventArray?.filter((ev2) => ev2.eventTypeGroupSequenceIdentity === ev.eventTypeGroupSequenceIdentity);
            evArrayForAllEventTypes = evArrayForAllEventTypes.concat(evArr);
        });
        return evArrayForAllEventTypes;
    };

    const getCargoEventsForPort = (eventArray: TankersEvent[], cargoOpArray: CargoOperation[]): TankersEvent[] => {
        const evArray = eventArray?.filter((ev) => {
            const eventTypeInputDropdownArray = ev.eventTypeInputRegList?.filter((drdownGr) => {
                return drdownGr.dropdownGroupReg;
            });

            const evInpGrArray = eventTypeInputDropdownArray.filter((evInpGr) => {
                return isCargoDropdown(evInpGr.dropdownGroupReg?.dropdownTypeId);
            });
            if (evInpGrArray && evInpGrArray.length > 0) {
                const dropdownGroupRegList = evInpGrArray.flatMap((eigr) => eigr.dropdownGroupReg);

                const evInpRegArray = dropdownGroupRegList.flatMap((ddgr) => ddgr && ddgr.dropdownItemRegList);

                const evInpReg = evInpRegArray.find((inpReg) => {
                    return cargoOpArray.find((cargoOpItem) => inpReg && cargoOpItem.id === inpReg.dropdownItemId);
                });
                return evInpReg;
            } else {
                return false;
            }
        });
        // Find events for all event types in event type group sequences found above
        let evArrayForAllEventTypes: TankersEvent[] = [];
        evArray.forEach((ev) => {
            const evArr = eventArray?.filter((ev2) => ev2.eventTypeGroupSequenceIdentity === ev.eventTypeGroupSequenceIdentity);
            evArrayForAllEventTypes = evArrayForAllEventTypes.concat(evArr);
        });
        return evArrayForAllEventTypes;
    };

    const filterOnPortCallIndex = (portCallId: number, eventArray: TankersEvent[], after: boolean): TankersEvent[] => {
        const pcInd = getPortCallIndex(portCallId);
        if (pcInd !== -1) {
            const evList = eventArray?.filter((ev) => {
                const ind = getPortCallIndex(ev.portCallId);
                if (ind > -1) {
                    return after ? ind > pcInd : ind < pcInd;
                }
                return false;
            });
            return evList;
        }
        return [];
    };

    const filterOnBerthCallIndex = (portCallId: number, berthCallId: number | null, eventArray: TankersEvent[], after: boolean): TankersEvent[] => {
        const bcInd = getBerthCallIndex(portCallId, berthCallId);
        if (bcInd !== -1) {
            const evList = eventArray.filter((ev) => {
                if (ev.portCallId && ev.berthCallId && ev.portCallId === portCallId) {
                    const ind = getBerthCallIndex(ev.portCallId, ev.berthCallId);
                    if (ind > -1) {
                        return after ? ind > bcInd : ind < bcInd;
                    }
                }
                return false;
            });
            return evList;
        }
        return [];
    };

    const filterOnCargoOpIndex = (
        portCallId: number,
        berthCallId: number,
        cargoOpId: number | null,
        eventArray: TankersEvent[],
        after: boolean
    ): TankersEvent[] => {
        const coInd = getCargoOperationIndex(portCallId, berthCallId, cargoOpId);
        if (coInd !== -1) {
            const evList = eventArray.filter((ev) => {
                if (ev.portCallId && ev.portCallId === portCallId && ev.berthCallId && ev.berthCallId === berthCallId) {
                    const ind = getCargoOperationIndex(ev.portCallId, ev.berthCallId, ev.cargoOperationId);
                    if (ind > -1) {
                        return after ? ind > coInd : ind < coInd;
                    }
                }
                return false;
            });
            return evList;
        }
        return [];
    };

    const filterOnNodeIndex = (node: IRotationNode, eventArray: TankersEvent[], after: boolean): TankersEvent[] => {
        if (node.portCall && node.portCall.portCallId) {
            if (node.type === RotationTreeEnum.PortCall || node.type === RotationTreeEnum.Sea) {
                return filterOnPortCallIndex(node.portCall.portCallId, eventArray, after);
            } else if (node.type === RotationTreeEnum.BerthCall) {
                return filterOnBerthCallIndex(node.portCall.portCallId, node.id, eventArray, after);
            } else if (node.type === RotationTreeEnum.CargoOperation) {
                const berthCall = getParentBerthCallFromNode(node);
                if (berthCall && berthCall.berthCallId) {
                    return filterOnCargoOpIndex(node.portCall.portCallId, berthCall.berthCallId, node.id, eventArray, after);
                }
            }
        }
        return [];
    };

    const filterOnPortCallId = (portCallId: number | null, eventArray: TankersEvent[]): TankersEvent[] => {
        if (portCallId) return eventArray.filter((ev) => ev.portCallId === portCallId);
        return [];
    };

    const filterOnCrossNode = (eventArray: TankersEvent[], evTypeGroup: EventTypeGroup, after: boolean): TankersEvent[] => {
        const evList = eventArray.filter((ev) => {
            if (after) {
                return evTypeGroup.crossPortCallPreviousEvTypeGroupList.find((o) => o === ev.eventType?.eventTypeGroupId) ?? false;
            } else {
                return evTypeGroup.crossPortCallNextEvTypeGroupList.find((o) => o === ev.eventType?.eventTypeGroupId) ?? false;
            }
        });
        return evList;
    };

    const findLatestEvent = (evList: TankersEvent[]) => {
        const evListSorted = evList.sort((a, b) => compareActualUtcEventDateTime(a, b, true));
        return evListSorted[0];
    };

    const findEarliestEvent = (evList: TankersEvent[]) => {
        const evListSorted = evList.sort((a, b) => compareActualUtcEventDateTime(a, b, false));
        return evListSorted[0];
    };

    const getRotationNodeFromEvent = (rot: Rotation, ev: TankersEvent | null): IRotationNode | null => {
        if (ev) {
            if (ev.eventTypeContextId === ReportLocationEnum.Sea) {
                return getPortCall(rot, ev.portCallId, ev.eventTypeContextId);
            } else if (ev.cargoOperationId) {
                return getCargoOperation(rot, ev.portCallId, ev.berthCallId, ev.cargoOperationId);
            } else if (ev.berthCallId) {
                return getBerthCall(rot, ev.portCallId, ev.berthCallId);
            }
            return getPortCall(rot, ev.portCallId, ev.eventTypeContextId);
        }
        return null;
    };

    return {
        getFirstPortCallTime,
        addEventToList,
        updateEventInList,
        deleteEventInList,
        deleteEventsForPortCallInList,
        deleteEventsForBerthCallInList,
        deleteEventsForCargoOperationInList,
        moveEventsToCargoOperationUpdate,
        moveEventsToBerthCallUpdate,
        moveEventsToPortCallUpdate,
        getFullEventArray,
        filterEventsOnDeleted,
        filterEventsOnActual,
        getCargoEventsForPort,
        getCargoEvents,
        isAtSea,
        getCurrentPortCall,
        getCurrentVoyage,
        getDefaultRotationTree,
        getHistoricRotationTree,
        getRotationTreeByVoyages,
        filterOnContext,
        filterOnContextIncludeParent,
        filterOnContextSingleLevel,
        filterOnContextSingleLevelIncludeParents,
        filterOnContextIncludeParentWithBerthCall,
        filterOnLocationContext,
        filterOnContextSeaIncludePort,
        filterSameEventTypeGroupAndLevel,
        addCargoEvents,
        filterOnContextSingleLevelIncludingCargoEvents,
        updateDeltaEventArray,
        filterOnPortCallIndex,
        filterOnNodeIndex,
        filterOnCrossNode,
        filterOnPortCallId,
        findLatestEvent,
        hasNoDelayOrNotExceededLimits,
        hasDelayInputItemReg,
        getRotationNodeFromEvent,
        nodeHasDelayAndMissingInput,
        nodeHasNotDelayAndExistingInput,
        eventHasDelayAndMissingInput,
        eventHasNoDelayAndExistingInput,
        eventHasDelayAndExistingInput,
        isDelayInput,
        isDelayExceeded,
        isCargoDropdown,
        isBerthDropdown,
    };
};
